import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { SCAN_TYPE_LABELS } from '@nirvik/shared';
import { useOrgContext } from '@/providers/org-provider';

interface Crumb {
  label: string;
  href?: string;
}

const AGENT_TYPE_LABELS: Record<string, string> = {
  'code-vuln': 'Code Vulnerability Scanner',
  dependency: 'Dependency Scanner',
  education: 'Security Education',
  'secret-scanner': 'Secret Scanner',
  'license-compliance': 'License Compliance',
  'container-security': 'Container Security',
  'iac-scanner': 'Infrastructure as Code',
  'api-security': 'API Security',
  sast: 'SAST Engine',
  'crypto-audit': 'Cryptography Audit',
  'quantum-readiness': 'Quantum Readiness',
  'access-control': 'Access Control Analysis',
  'data-privacy': 'Data Privacy',
  'supply-chain': 'Supply Chain Security',
  'cloud-config': 'Cloud Configuration',
  'threat-model': 'AI Threat Model',
  compliance: 'Compliance Mapping',
  'pentest-recon': 'Attack Surface Recon',
  'malware-detect': 'Malicious Code Detection',
  'config-audit': 'Configuration Audit',
  'network-security': 'Network Security',
  'incident-response': 'Incident Response Readiness',
  'security-score': 'Security Scorecard',
  'cicd-gate': 'CI/CD Security Gate',
};

/**
 * Breadcrumbs for the org/portfolio/project hierarchy. Matches paths of
 * the form /orgs/:orgId[/portfolios/:portfolioId[/projects/:projectId[...]]].
 */
export function Breadcrumbs() {
  const location = useLocation();
  const params = useParams();
  const path = location.pathname;
  const { currentOrg } = useOrgContext();

  const orgId = params.orgId as string | undefined;
  const portfolioId = params.portfolioId as string | undefined;
  const projectId = params.projectId as string | undefined;

  const fullScope = !!orgId && !!portfolioId && !!projectId;

  const { data: portfolioData } = useQuery({
    queryKey: ['portfolio', orgId, portfolioId],
    queryFn: () => apiClient.getPortfolio(orgId!, portfolioId!),
    enabled: !!orgId && !!portfolioId,
    staleTime: 60_000,
  });
  const portfolioName = (portfolioData?.data as { name?: string } | undefined)?.name;

  const { data: projectData } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled: fullScope,
    staleTime: 60_000,
  });
  const projectName = (projectData?.data as { name?: string } | undefined)?.name;

  // Scan detail breadcrumb
  const scanIdMatch = path.match(/\/scans\/([^/]+)\/?$/);
  const scanId = scanIdMatch?.[1];
  const { data: scanData } = useQuery({
    queryKey: ['scan', orgId, portfolioId, projectId, scanId],
    queryFn: () => apiClient.getScan(orgId!, portfolioId!, projectId!, scanId!),
    enabled: fullScope && !!scanId && scanId !== 'new',
    staleTime: 60_000,
  });
  const scanType = (scanData?.data as { scanType?: string } | undefined)?.scanType;
  const scanLabel = scanType
    ? SCAN_TYPE_LABELS[scanType as keyof typeof SCAN_TYPE_LABELS] || scanType
    : 'Scan';

  // Agent run detail breadcrumb
  const runIdMatch = path.match(/\/agents\/runs\/([^/]+)\/?$/);
  const runId = runIdMatch?.[1];
  const { data: runData } = useQuery({
    queryKey: ['agent-run', orgId, portfolioId, projectId, runId],
    queryFn: () => apiClient.get(`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/runs/${runId}`),
    enabled: fullScope && !!runId,
    staleTime: 60_000,
  });
  const runAgentType = (runData?.data as { run?: { agentType?: string } })?.run?.agentType;
  const runAgentLabel = runAgentType ? (AGENT_TYPE_LABELS[runAgentType] || runAgentType) : undefined;

  const agentTypeMatch = path.match(/\/agents\/([^/]+)\/?$/);
  const agentType = agentTypeMatch?.[1] !== 'runs' ? agentTypeMatch?.[1] : undefined;

  const crumbs = buildCrumbs(path, {
    orgId,
    orgName: currentOrg?.name,
    portfolioId,
    portfolioName,
    projectId,
    projectName,
    scanLabel,
    agentType,
    runAgentType,
    runAgentLabel,
  });

  if (crumbs.length <= 1) return null;

  return (
    <nav className="flex items-center gap-1.5 text-sm mb-6 flex-wrap">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            {isLast ? (
              <span className="font-medium text-foreground truncate max-w-48">{crumb.label}</span>
            ) : crumb.href ? (
              <Link
                to={crumb.href}
                className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-48"
              >
                {i === 0 ? (
                  <span className="flex items-center gap-1.5">
                    <Home className="h-3.5 w-3.5" />
                    {crumb.label}
                  </span>
                ) : (
                  crumb.label
                )}
              </Link>
            ) : (
              <span className="text-muted-foreground truncate max-w-48">{crumb.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

interface BuildCrumbsContext {
  orgId?: string;
  orgName?: string;
  portfolioId?: string;
  portfolioName?: string;
  projectId?: string;
  projectName?: string;
  scanLabel?: string;
  agentType?: string;
  runAgentType?: string;
  runAgentLabel?: string;
}

function buildCrumbs(path: string, ctx: BuildCrumbsContext): Crumb[] {
  const {
    orgId, orgName, portfolioId, portfolioName, projectId, projectName,
    scanLabel, agentType, runAgentType, runAgentLabel,
  } = ctx;

  const crumbs: Crumb[] = [];
  if (orgId) {
    crumbs.push({ label: orgName || 'Org', href: `/orgs/${orgId}/dashboard` });
  } else {
    crumbs.push({ label: 'Home', href: '/home' });
    if (path === '/my/notifications') {
      crumbs.push({ label: 'Notifications' });
    } else if (path === '/my/settings') {
      crumbs.push({ label: 'Account Settings' });
    } else if (path === '/my/learn') {
      crumbs.push({ label: 'Learn' });
    } else if (path === '/my/learn/scoring') {
      crumbs.push({ label: 'Learn', href: '/my/learn' });
      crumbs.push({ label: 'How security scores work' });
    } else if (path === '/my/learn/quantum-readiness') {
      crumbs.push({ label: 'Learn', href: '/my/learn' });
      crumbs.push({ label: 'Post-quantum readiness' });
    } else if (path === '/my/learn/findings-lifecycle') {
      crumbs.push({ label: 'Learn', href: '/my/learn' });
      crumbs.push({ label: 'Findings lifecycle' });
    } else if (path.startsWith('/my/learn/')) {
      crumbs.push({ label: 'Learn', href: '/my/learn' });
      crumbs.push({ label: 'Lesson' });
    }
    return crumbs;
  }

  // Global pages — even when an org is resolved (now always the case
  // via OrgProvider's localStorage fallback), these routes don't sit
  // under /orgs/:id/. Show "Workspace > <Page>" so the user always has
  // a one-click path back to the org.
  if (path === '/my/notifications') {
    crumbs.push({ label: 'Notifications' });
    return crumbs;
  }
  if (path === '/my/settings') {
    crumbs.push({ label: 'Account Settings' });
    return crumbs;
  }
  if (path === '/my/learn') {
    crumbs.push({ label: 'Learn' });
    return crumbs;
  }
  if (path === '/my/learn/scoring') {
    crumbs.push({ label: 'Learn', href: '/my/learn' });
    crumbs.push({ label: 'How security scores work' });
    return crumbs;
  }
  if (path.startsWith('/my/learn/')) {
    crumbs.push({ label: 'Learn', href: '/my/learn' });
    crumbs.push({ label: 'Lesson' });
    return crumbs;
  }
  // Top-level org pages
  if (path === `/orgs/${orgId}/dashboard`) return crumbs;
  if (path === `/orgs/${orgId}/settings`) {
    crumbs.push({ label: 'Settings' });
    return crumbs;
  }
  if (path === `/orgs/${orgId}/portfolios`) {
    crumbs.push({ label: 'Portfolios' });
    return crumbs;
  }
  if (path === `/orgs/${orgId}/tep`) {
    crumbs.push({ label: 'Compliance Posture' });
    return crumbs;
  }

  if (portfolioId) {
    crumbs.push({ label: 'Portfolios', href: `/orgs/${orgId}/portfolios` });

    if (path === `/orgs/${orgId}/portfolios/${portfolioId}`) {
      crumbs.push({ label: portfolioName || 'Portfolio' });
      return crumbs;
    }

    crumbs.push({ label: portfolioName || 'Portfolio', href: `/orgs/${orgId}/portfolios/${portfolioId}` });

    if (path.endsWith('/projects/new')) {
      crumbs.push({ label: 'New Project' });
      return crumbs;
    }

    if (projectId) {
      const projectBase = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;
      if (path === projectBase) {
        crumbs.push({ label: projectName || 'Project' });
        return crumbs;
      }
      crumbs.push({ label: projectName || 'Project', href: projectBase });

      if (path === `${projectBase}/scans`) { crumbs.push({ label: 'Scans' }); return crumbs; }
      if (path === `${projectBase}/scans/new`) { crumbs.push({ label: 'Scans', href: `${projectBase}/scans` }); crumbs.push({ label: 'New Scan' }); return crumbs; }
      if (path.match(new RegExp(`^${projectBase}/scans/[^/]+$`))) {
        crumbs.push({ label: 'Scans', href: `${projectBase}/scans` });
        crumbs.push({ label: scanLabel || 'Scan Results' });
        return crumbs;
      }
      if (path === `${projectBase}/findings`) { crumbs.push({ label: 'Findings' }); return crumbs; }
      if (path === `${projectBase}/report`) { crumbs.push({ label: 'Report' }); return crumbs; }
      if (path === `${projectBase}/agents`) { crumbs.push({ label: 'Agents' }); return crumbs; }
      if (path.match(new RegExp(`^${projectBase}/agents/runs/[^/]+$`))) {
        crumbs.push({ label: 'Agents', href: `${projectBase}/agents` });
        if (runAgentType) crumbs.push({ label: runAgentLabel || runAgentType, href: `${projectBase}/agents/${runAgentType}` });
        crumbs.push({ label: 'Run Details' });
        return crumbs;
      }
      if (path.match(new RegExp(`^${projectBase}/agents/[^/]+$`))) {
        crumbs.push({ label: 'Agents', href: `${projectBase}/agents` });
        crumbs.push({ label: agentType ? (AGENT_TYPE_LABELS[agentType] || agentType) : 'Agent' });
        return crumbs;
      }
      if (path === `${projectBase}/settings`) { crumbs.push({ label: 'Settings' }); return crumbs; }
    }
  }

  return crumbs;
}
